import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { UserLimitEnforcerModal } from './user-limit-enforcer-modal';
import { useUserHasAccountManagementRole, useUserHasAdministrationRole } from '../../../hooks/use-user-role';
import { UNLIMITED_PLAN_LIMIT, useUserLimitData } from '../../../pages/settings/payment-and-subscription/hooks/use-plan-details';
import Global from '../../../store/global-selectors';

/**
 * App-level user limit enforcer component
 * Monitors when active user count exceeds billing plan limit and displays blocking modal
 * @returns {React.JSX.Element | null}
 */
export const UserLimitEnforcer = () => {
  const { baseSupportedUserCount, hasUnlimitedUsers, totalUserCount } = useUserLimitData();
  const activeUsers = useSelector(Global.selectSellerActiveUsers);
  const isAdministrator = useUserHasAdministrationRole();
  const hasAccountManagement = useUserHasAccountManagementRole();
  const isSystemAdmin = useSelector(Global.selectUserisSysAdmin);
  const subscriptionData = useSelector(Global.selectSellerSubscriptionData);
  const isOnAnnualBilling = subscriptionData?.currentBillingPlan?.isOnAnnualBilling ?? false;

  const hasOpenedRef = useRef(false);
  const [isDismissed, setIsDismissed] = useState(false);

  const hasBillingPermissions = isAdministrator || hasAccountManagement;

  const isUserLimitExceeded = useMemo(() => {
    if (!isOnAnnualBilling) {
      return false;
    }

    if (baseSupportedUserCount === UNLIMITED_PLAN_LIMIT || hasUnlimitedUsers) {
      return false;
    }

    return activeUsers.length > totalUserCount;
  }, [activeUsers.length, baseSupportedUserCount, hasUnlimitedUsers, isOnAnnualBilling, totalUserCount]);

  if (isUserLimitExceeded) {
    hasOpenedRef.current = true;
  }

  const handleDismiss = useCallback(() => setIsDismissed(true), []);

  if (!hasOpenedRef.current || isDismissed) {
    return null;
  }

  return (
    <UserLimitEnforcerModal
      canDismiss={isSystemAdmin}
      hasBillingPermissions={hasBillingPermissions}
      maxAllowedUsers={totalUserCount}
      onDismiss={handleDismiss}
    />
  );
};
